import React, { useState } from "react";

import {
  Alert,
  Box,
  Typography,
  Tabs,
  Tab
} from "@mui/material";
import { ContractsSection } from "./contractsSection";
import { InventoryPoolsSection } from "./inventoryPoolsSection";
import { PortfoliosSection } from "./portfoliosSection";
import {
  useFetchContracts,
  useFetchSuppliers,
  useFetchProjects,
  useFetchInventoryPools,
  useFetchPortfolios,
  useFetchClients,
  useCreateContract,
  useUpdateContract,
  useCreateSupplier,
  useCreateProject,
  useCreateInventoryPool,
  useCreatePortfolio,
  useUpdatePortfolioMix,
  useUpdatePortfolioPrice,
  useAddPortfolioClients,
  useCreateClient,
  useVoidContract
} from "./hooks";

export default function CarbonOffsetManagement() {
  const [activeTab, setActiveTab] = useState(0);
  const [reorderError, setReorderError] = useState(null);

  const { data: contracts = [] } = useFetchContracts();
  const { data: suppliers = [] } = useFetchSuppliers();
  const { data: projects = [] } = useFetchProjects();
  const { data: inventoryPools = [] } = useFetchInventoryPools();
  const { data: portfolios = [] } = useFetchPortfolios();
  const { data: clients = [] } = useFetchClients();

  const { mutate: createContract } = useCreateContract();
  const { mutate: updateContract, mutateAsync: updateContractAsync } = useUpdateContract();
  const { mutate: voidContract } = useVoidContract();
  const { mutateAsync: createSupplierAsync } = useCreateSupplier();
  const { mutateAsync: createProjectAsync } = useCreateProject();
  const { mutate: createInventoryPool } = useCreateInventoryPool();
  const { mutate: createPortfolio } = useCreatePortfolio();
  const { mutate: updatePortfolioMix } = useUpdatePortfolioMix();
  const { mutate: updatePortfolioPrice } = useUpdatePortfolioPrice();
  const { mutate: addPortfolioClients } = useAddPortfolioClients();
  const { mutateAsync: createClientAsync } = useCreateClient();

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "grey.50" }}>
      <Box sx={{ borderBottom: 1, borderColor: "divider", bgcolor: "background.paper" }}>
        <Box sx={{ px: 3, py: 2 }}>
          <Typography variant="h4" fontWeight="bold">Carbon Offset Management</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage contracts, portfolios, and track retirements
          </Typography>
        </Box>
      </Box>

      <Box sx={{ px: 3, py: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{ mb: 3 }}
        >
          <Tab label="Contracts" />
          <Tab label="Inventory Pools" />
          <Tab label="Portfolios" />
        </Tabs>

        {activeTab === 0 && (
          <ContractsSection
            suppliers={suppliers}
            projects={projects}
            contracts={contracts}
            inventoryPools={inventoryPools}
            onAddContract={(data) => createContract(data)}
            onAddSupplier={(data) => createSupplierAsync(data)}
            onAddProject={(data) => createProjectAsync(data)}
            onUpdateContract={({ id, data }) => updateContract({ id, data })}
            onVoidContract={(id) => voidContract(id)}
          />
        )}

        {activeTab === 1 && (
          <>
            {reorderError && (
              <Alert severity="error" onClose={() => setReorderError(null)} sx={{ mb: 2 }}>
                {reorderError}
              </Alert>
            )}
            <InventoryPoolsSection
              inventoryPools={inventoryPools}
              contracts={contracts}
              suppliers={suppliers}
              projects={projects}
              onAddInventoryPool={(data) => createInventoryPool(data)}
              onUpdateDrawdownOrder={(poolId, orders) => {
                setReorderError(null);
                Promise.all(
                  orders.map(({ contractId, order }) =>
                    updateContractAsync({ id: contractId, data: { projectDrawDownOrder: order } })
                  )
                ).catch((err) => setReorderError(err?.message || "Failed to save order"));
              }}
              onNavigateToTab={setActiveTab}
            />
          </>
        )}

        {activeTab === 2 && (
          <PortfoliosSection
            portfolios={portfolios}
            clients={clients}
            inventoryPools={inventoryPools}
            projects={projects}
            contracts={contracts}
            onAddPortfolio={(data) => createPortfolio(data)}
            onUpdatePortfolioMix={({ id, data }) => updatePortfolioMix({ id, data })}
            onUpdatePortfolioPrice={({ id, data }) => updatePortfolioPrice({ id, data })}
            onAddPortfolioClients={({ id, clients }) => addPortfolioClients({ id, clients })}
            onAddClient={(data) => createClientAsync(data)}
          />
        )}
      </Box>
    </Box>
  );
}
